import { useState } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { CustomerSpeakWidget } from '@/shared/widgets/customer-speak/CustomerSpeakWidget';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { PDP_DESKTOP_PROMISE_BADGES } from '../data/pdpDesktopPromise.data';
import { BsPromisesSheet } from './BsPromisesSheet';

function PromiseBadge({
  title,
  href,
  Icon,
}: {
  title: string;
  href?: string;
  Icon: (typeof PDP_DESKTOP_PROMISE_BADGES)[number]['Icon'];
}) {
  const content = (
    <>
      <Icon
        aria-hidden
        className="h-5 w-5 shrink-0 text-[#4476b0]"
        strokeWidth={1.75}
      />
      <span className="text-[11px] font-semibold uppercase leading-tight tracking-[0.02em] text-[#e26a5e]">
        {title}
      </span>
    </>
  );

  if (href) {
    return (
      <AppLink
        to={href}
        shouldRefresh={false}
        target="_blank"
        className="flex min-h-10 items-center gap-2 no-underline hover:opacity-90"
      >
        {content}
      </AppLink>
    );
  }

  return <div className="flex min-h-10 items-center gap-2">{content}</div>;
}

interface PdpDesktopPromiseCardProps {
  onKnowMoreClick?: () => void;
  showCustomerSpeak?: boolean;
}

export function PdpDesktopPromiseCard({ onKnowMoreClick, showCustomerSpeak = true }: PdpDesktopPromiseCardProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
  };

  const { goBack } = usePopupHistory({
    isOpen: isSheetOpen,
    onClose: handleCloseSheet,
    stateKey: 'bspromise-desktop',
  });

  const handleKnowMore = () => {
    onKnowMoreClick?.();
    setIsSheetOpen(true);
  };

  return (
    <>
      <div className="flex w-full min-w-0 flex-col gap-4">
        <section className="border border-[#e2e2e2] bg-white px-4 py-5">
          <h3 className="mb-5 text-sm font-bold uppercase tracking-[0.06em] text-black">
            Bluestone Promise
          </h3>
          <div className="grid grid-cols-3 gap-x-3 gap-y-5">
            {PDP_DESKTOP_PROMISE_BADGES.map((badge) => (
              <PromiseBadge
                key={badge.id}
                title={badge.title}
                href={badge.href}
                Icon={badge.Icon}
              />
            ))}
          </div>
          <div className="mt-5 border-t border-[#e2e2e2] text-center">
            <button
              type="button"
              onClick={handleKnowMore}
              className="w-full py-3 text-sm font-normal text-[var(--link-blue)]"
            >
              Know more
            </button>
          </div>
        </section>

        <section className="bg-[#9fb1cc] px-6 py-8 text-center">
          <h4 className="mb-4 text-sm font-bold uppercase tracking-[0.04em] text-[#1f2a44]">
            Certificate of Authenticity
          </h4>
          <p className="mx-auto max-w-[320px] font-serif text-sm leading-[1.55] text-white [&_strong]:font-bold">
            Every piece of jewellery that we make is certified for authenticity by third-party
            international laboratories like <strong>IGI, BIS, GIA, and GSI.</strong>
          </p>
        </section>

        {showCustomerSpeak && <CustomerSpeakWidget />}
      </div>

      <BsPromisesSheet open={isSheetOpen} onClose={goBack} />
    </>
  );
}
